import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Eye, EyeOff, Users, User, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardVisibilityPanel({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('role');
  const [widgets, setWidgets] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleSettings, setRoleSettings] = useState({});
  const [selectedRole, setSelectedRole] = useState('account_executive');

  // User-level
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [userSettings, setUserSettings] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, rRes, uRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard-visibility/widgets`, { credentials: 'include' }),
        fetch(`${API_URL}/api/dashboard-visibility`, { credentials: 'include' }),
        fetch(`${API_URL}/api/users`, { credentials: 'include' }),
      ]);
      if (wRes.ok) {
        const d = await wRes.json();
        setWidgets(d.widgets || []);
        setRoles(d.roles || []);
      }
      if (rRes.ok) {
        setRoleSettings(await rRes.json());
      }
      if (uRes.ok) {
        setUsers(await uRes.json());
      }
    } catch (e) { logger.error('Fetch visibility error', e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const fetchUserOverrides = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const r = await fetch(`${API_URL}/api/dashboard-visibility/user/${uid}`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setUserSettings(d.widgets || {});
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (selectedUser) fetchUserOverrides(selectedUser);
  }, [selectedUser, fetchUserOverrides]);

  // Toggle role widget
  const toggleRole = (widgetId) => {
    setRoleSettings(prev => {
      const current = prev[selectedRole] || {};
      const allDefaults = Object.fromEntries(widgets.map(w => [w.id, true]));
      const merged = { ...allDefaults, ...current };
      return { ...prev, [selectedRole]: { ...merged, [widgetId]: !merged[widgetId] } };
    });
  };

  // Toggle user widget
  const toggleUser = (widgetId) => {
    setUserSettings(prev => {
      const val = prev[widgetId];
      if (val === undefined) return { ...prev, [widgetId]: false };
      if (val === false) return { ...prev, [widgetId]: true };
      // If true, remove override (go back to role default)
      const next = { ...prev };
      delete next[widgetId];
      return next;
    });
  };

  const saveRoleSettings = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/dashboard-visibility/${selectedRole}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ widgets: roleSettings[selectedRole] || {} }),
      });
      if (r.ok) toast.success(`Saved ${selectedRole} visibility`);
      else toast.error('Failed to save');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const saveUserSettings = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/dashboard-visibility/user/${selectedUser}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ widgets: userSettings }),
      });
      if (r.ok) toast.success('Saved user visibility overrides');
      else toast.error('Failed to save');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const getRoleLabel = (r) => r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const currentRoleWidgets = roleSettings[selectedRole] || {};
  const allDefaults = Object.fromEntries(widgets.map(w => [w.id, true]));
  const mergedRole = { ...allDefaults, ...currentRoleWidgets };

  // Group widgets by group
  const grouped = {};
  for (const w of widgets) {
    if (!grouped[w.group]) grouped[w.group] = [];
    grouped[w.group].push(w);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" /> Dashboard Widget Visibility
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="role" className="flex items-center gap-1.5" data-testid="visibility-tab-role">
                <Users className="h-3.5 w-3.5" /> By Role
              </TabsTrigger>
              <TabsTrigger value="user" className="flex items-center gap-1.5" data-testid="visibility-tab-user">
                <User className="h-3.5 w-3.5" /> By User
              </TabsTrigger>
            </TabsList>

            {/* Role-level */}
            <TabsContent value="role" className="space-y-4 mt-4">
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger data-testid="visibility-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map(r => <SelectItem key={r} value={r}>{getRoleLabel(r)}</SelectItem>)}
                </SelectContent>
              </Select>

              {Object.entries(grouped).map(([group, items]) => (
                <div key={group} className="space-y-2">
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{group}</h4>
                  <div className="space-y-1">
                    {items.map(w => (
                      <div key={w.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`role-widget-${w.id}`}>
                        <div className="flex items-center gap-2">
                          {mergedRole[w.id] ? <Eye className="h-4 w-4 text-emerald-500" /> : <EyeOff className="h-4 w-4 text-slate-300" />}
                          <span className="text-sm text-slate-700">{w.label}</span>
                        </div>
                        <Switch checked={mergedRole[w.id] !== false} onCheckedChange={() => toggleRole(w.id)} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="pt-2">
                <Button onClick={saveRoleSettings} disabled={saving} className="w-full" data-testid="save-role-visibility">
                  {saving ? 'Saving...' : `Save ${getRoleLabel(selectedRole)} Settings`}
                </Button>
              </div>
            </TabsContent>

            {/* User-level */}
            <TabsContent value="user" className="space-y-4 mt-4">
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger data-testid="visibility-user-select"><SelectValue placeholder="Select a user..." /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}
                </SelectContent>
              </Select>

              {selectedUser ? (
                <>
                  <p className="text-xs text-slate-500">Override role defaults for this user. Click toggle 3 times to cycle: Role Default → Hidden → Visible → Role Default.</p>
                  {Object.entries(grouped).map(([group, items]) => (
                    <div key={group} className="space-y-2">
                      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{group}</h4>
                      <div className="space-y-1">
                        {items.map(w => {
                          const hasOverride = userSettings[w.id] !== undefined;
                          const isVisible = hasOverride ? userSettings[w.id] : true;
                          return (
                            <div key={w.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50" data-testid={`user-widget-${w.id}`}>
                              <div className="flex items-center gap-2">
                                {isVisible ? <Eye className="h-4 w-4 text-emerald-500" /> : <EyeOff className="h-4 w-4 text-slate-300" />}
                                <span className="text-sm text-slate-700">{w.label}</span>
                                {hasOverride && <Badge variant="outline" className="text-[10px] px-1.5 py-0">override</Badge>}
                              </div>
                              <Switch checked={isVisible} onCheckedChange={() => toggleUser(w.id)} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <div className="pt-2">
                    <Button onClick={saveUserSettings} disabled={saving} className="w-full" data-testid="save-user-visibility">
                      {saving ? 'Saving...' : 'Save User Overrides'}
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-center text-slate-400 py-8">Select a user to manage their widget visibility</p>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
